import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useRequest from '../hooks/useRequest';
import { useAuth } from './AuthContext';

const BookmarkContext = createContext();

export const useBookmark = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error('useBookmark must be used within a BookmarkProvider');
  }
  return context;
};

export const BookmarkProvider = ({ children }) => {
  const { sendRequest } = useRequest();
  const { user, refreshUserData } = useAuth();
  
  const [bookmarks, setBookmarks] = useState({
    products: new Map(),
    posts: new Map(),
    pharmacies: new Map()
  });
  
  // Collections now come from AuthContext
  const collections = user?.collections || [];
  const [loading, setLoading] = useState(false);
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);

  const processBookmarksData = useCallback((bookmarksData) => {
    if (!Array.isArray(bookmarksData)) return;
    
    const newBookmarks = {
      products: new Map(),
      posts: new Map(),
      pharmacies: new Map()
    };

    bookmarksData.forEach(bookmark => {
      const key = bookmark.elementUUID || bookmark.integrationId;
      const type = parseInt(bookmark.type);
      switch (type) {
        case 0:
          newBookmarks.posts.set(key, bookmark);
          break;
        case 1:
          newBookmarks.products.set(key, bookmark);
          break;
        case 2:
          newBookmarks.pharmacies.set(key, bookmark);
          break;
      }
    });

    setBookmarks(newBookmarks);
    setBookmarksLoaded(true);
  }, []);

  const fetchAllBookmarks = useCallback(async () => {
    if (!user || !user.token) return;

    setLoading(true);
    try {
      const response = await sendRequest({
        method: 'GET',
        url: '/module/bookmark/getAllBookmarks',
      });

      if (response.status === 'success') {
        processBookmarksData(response.data);
      }
    } catch (error) {
      console.error('BookmarkContext - Error fetching bookmarks:', error);
    } finally {
      setLoading(false);
    }
  }, [user, sendRequest, processBookmarksData]);

  const setBookmarksData = useCallback((bookmarksData) => {
    processBookmarksData(bookmarksData);
  }, [processBookmarksData]);

  const setCollectionsData = useCallback((collectionsData) => {
    // Collections are now managed by AuthContext, this is kept for compatibility
    console.log('setCollectionsData called but collections are now in AuthContext');
  }, []);

  const fetchCollections = useCallback(async () => {
    // Collections are now fetched by AuthContext, this is kept for compatibility
    console.log('fetchCollections called but collections are now in AuthContext');
  }, []);

  const isBookmarked = useCallback((elementUUID, type) => {
    switch (type) {
      case 0:
        return bookmarks.posts.has(elementUUID);
      case 1:
        return bookmarks.products.has(elementUUID);
      case 2:
        return bookmarks.pharmacies.has(elementUUID);
      default:
        return false;
    }
  }, [bookmarks]);

  const getBookmarkStatus = useCallback((elementUUID, type) => {
    return isBookmarked(elementUUID, type) ? 'success' : 'error';
  }, [isBookmarked]);

  const getBookmarkStatuses = useCallback((items, type) => {
    const statuses = {};
    items.forEach(item => {
      const elementUUID = item.integrationId || item.elementUUID;
      statuses[elementUUID] = getBookmarkStatus(elementUUID, type);
    });
    return statuses;
  }, [getBookmarkStatus]);

  const addBookmark = useCallback(async (elementUUID, type, collectionUUID = null) => {
    try {
      const requestData = {
        type,
        elementUUID
      };
      
      if (collectionUUID) {
        requestData.collectionUUID = collectionUUID;
      }

      const response = await sendRequest({
        method: 'POST',
        url: '/module/bookmark/addBookmark',
        data: requestData
      });

      if (response.status === 'success') {
        const newBookmark = { integrationId: elementUUID, type, collectionUUID };
        setBookmarks(prev => {
          const updated = { ...prev };
          switch (type) {
            case 0:
              updated.posts.set(elementUUID, newBookmark);
              break;
            case 1:
              updated.products.set(elementUUID, newBookmark);
              break;
            case 2:
              updated.pharmacies.set(elementUUID, newBookmark);
              break;
          }
          return updated;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error adding bookmark:', error);
      return false;
    }
  }, [sendRequest]);

  const removeBookmark = useCallback(async (elementUUID, type) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/bookmark/removeBookmark',
        data: { elementUUID, type }
      });

      if (response.status === 'success') {
        setBookmarks(prev => {
          const updated = { ...prev };
          switch (type) {
            case 0:
              updated.posts.delete(elementUUID);
              break;
            case 1:
              updated.products.delete(elementUUID);
              break;
            case 2:
              updated.pharmacies.delete(elementUUID);
              break;
          }
          return updated;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing bookmark:', error);
      return false;
    }
  }, [sendRequest]);

  const toggleBookmark = useCallback(async (elementUUID, type, collectionUUID = null) => {
    const isCurrentlyBookmarked = isBookmarked(elementUUID, type);
    
    if (isCurrentlyBookmarked) {
      return await removeBookmark(elementUUID, type);
    } else {
      return await addBookmark(elementUUID, type, collectionUUID);
    }
  }, [isBookmarked, addBookmark, removeBookmark]);

  const createCollection = useCallback(async (name) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/bookmarkcollection/createCollection',
        data: { name }
      });
      
      if (response.status === 'success') {
        // Refresh user data to get updated collections
        await refreshUserData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error creating collection:', error);
      return false;
    }
  }, [sendRequest, refreshUserData]);

  const getBookmarkedItems = useCallback((type) => {
    switch (type) {
      case 0:
        return Array.from(bookmarks.posts.keys());
      case 1:
        return Array.from(bookmarks.products.keys());
      case 2:
        return Array.from(bookmarks.pharmacies.keys());
      default:
        return [];
    }
  }, [bookmarks]);

  useEffect(() => {
    if (!user || !user.token) {
      setBookmarks({
        products: new Map(),
        posts: new Map(),
        pharmacies: new Map()
      });
      // Collections are now in AuthContext, no need to clear here
      setBookmarksLoaded(false);
    }
  }, [user]);

  // Debug logging removed - re-enable if needed for debugging
  // useEffect(() => {
  //   console.log('BookmarkContext - User:', !!user, 'Bookmarks loaded:', bookmarks.products.size + bookmarks.posts.size + bookmarks.pharmacies.size);
  //   console.log('BookmarkContext - Post bookmarks:', Array.from(bookmarks.posts.keys()));
  // }, [user, bookmarks]);

  const value = {
    bookmarks,
    collections,
    loading,
    bookmarksLoaded,
    isBookmarked,
    getBookmarkStatus,
    getBookmarkStatuses,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    createCollection,
    getBookmarkedItems,
    refreshBookmarks: fetchAllBookmarks,
    refreshCollections: fetchCollections,
    setBookmarksData,
    setCollectionsData
  };

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};